import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useStore } from '../../../lib/store'; // FIXED: Correct import path

interface BikeData {
  id?: string;
  model?: string;
  rego_number?: string;
  daily_rate?: number;
  rate_a?: number;
  rate_b?: number;
  status?: 'available' | 'rented' | 'maintenance' | 'retired';
  notes?: string;
  service_notes?: string;
  rego_expiry?: string;
  last_service?: string;
  service_centre?: string;
  make?: string;
  insurance_details?: Record<string, any>;
}

// Helper function to safely convert any value to string
const safeStringConvert = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join('\n');
  return String(value);
};

export function EditBikeDetails() {
  const { bikeId } = useParams<{ bikeId: string }>();
  const navigate = useNavigate();
  const updateBike = useStore((state) => state.updateBike);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [bikeData, setBikeData] = useState<BikeData>({
    model: '',
    rego_number: '',
    daily_rate: 0,
    rate_a: 0,
    rate_b: 0,
    status: 'available',
    notes: '',
    service_notes: '',
    rego_expiry: '',
    last_service: '',
    service_centre: '',
    make: '',
  });

  useEffect(() => {
    fetchBikeData();
  }, [bikeId]);

  const fetchBikeData = async () => {
    if (!bikeId) {
      setError('Bike ID is missing');
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 Fetching bike with ID:', bikeId);
      
      const { data, error } = await supabase
        .from('bikes')
        .select('*')
        .eq('id', bikeId)
        .single();

      if (error) {
        console.error('❌ Supabase fetch error:', error);
        throw new Error(`Error fetching bike: ${error.message}`);
      }

      console.log('✅ Raw bike data from database:', data);

      if (data) {
        // Safe data processing
        const processedServiceNotes = safeStringConvert(data.service_notes);
        
        const cleanData: BikeData = {
          id: data.id,
          make: safeStringConvert(data.make),
          model: safeStringConvert(data.model),
          rego_number: safeStringConvert(data.rego_number),
          daily_rate: Number(data.daily_rate) || 0,
          rate_a: Number(data.rate_a) || 0,
          rate_b: Number(data.rate_b) || 0,
          status: data.status || 'available',
          notes: safeStringConvert(data.notes),
          service_notes: processedServiceNotes,
          rego_expiry: data.rego_expiry || '',
          last_service: data.last_service || '',
          service_centre: safeStringConvert(data.service_centre),
          insurance_details: data.insurance_details || {},
        };
        
        console.log('✅ Cleaned bike data:', cleanData);
        setBikeData(cleanData);
      } else {
        setError('Bike not found');
      }
    } catch (err) {
      console.error('❌ Error in fetchBikeData:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setBikeData(prev => {
      // Handle numeric fields
      if (name === 'daily_rate' || name === 'rate_a' || name === 'rate_b') {
        const numValue = value === '' ? 0 : parseFloat(value);
        const finalValue = isNaN(numValue) ? 0 : numValue;
        return {
          ...prev,
          [name]: finalValue
        };
      }
      
      // Handle all other fields as strings
      return {
        ...prev,
        [name]: value || ''
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveBikeData();
  };

  const saveBikeData = async () => {
    if (!bikeId) {
      setError('Bike ID is missing');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      console.log('💾 Starting save process...');
      console.log('📋 Current bikeData before save:', bikeData);
      
      // Prepare clean update data - let the store handle ALL processing
      const updateData = {
        make: bikeData.make,
        model: bikeData.model,
        rego_number: bikeData.rego_number,
        daily_rate: bikeData.daily_rate,
        rate_a: bikeData.rate_a,
        rate_b: bikeData.rate_b,
        status: bikeData.status,
        notes: bikeData.notes,
        rego_expiry: bikeData.rego_expiry || null,
        last_service: bikeData.last_service || null,
        // Only include service_centre if it has a value and matches expected format
        ...(bikeData.service_centre && bikeData.service_centre.trim() !== '' && {
          service_centre: bikeData.service_centre.trim()
        }),
        service_notes: bikeData.service_notes, // Store will handle conversion safely
      };

      console.log('📤 Sending update data to store:', updateData);

      // ONLY use the store's updateBike function - no direct Supabase calls
      await updateBike(bikeId, updateData);
      
      console.log('✅ Store update successful');
      console.log('🏠 Navigating back to bikes list...');
      
      // Navigate back to bikes list
      navigate('/bikes');
      
    } catch (err) {
      console.error('❌ Error in saveBikeData:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred during save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading bike details...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-600 mb-4">Error: {error}</div>
        <button 
          onClick={() => navigate('/bikes')}
          className="bg-gray-300 px-4 py-2 rounded"
        >
          Back to Bikes
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Edit Bike Details</h1>
      
      {saving && (
        <div className="mb-4 p-3 bg-blue-100 text-blue-700 rounded border border-blue-300">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
            💾 Saving changes...
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block mb-1 font-medium">Make *</label>
          <input
            type="text"
            name="make"
            value={bikeData.make || ''}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block mb-1 font-medium">Model *</label>
          <input
            type="text"
            name="model"
            value={bikeData.model || ''}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block mb-1 font-medium">Registration Number *</label>
          <input
            type="text"
            name="rego_number"
            value={bikeData.rego_number || ''}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
          />
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block mb-1 font-medium">Daily Rate *</label>
            <input
              type="number"
              name="daily_rate"
              value={bikeData.daily_rate || ''}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block mb-1 font-medium">Rate A</label>
            <input
              type="number"
              name="rate_a"
              value={bikeData.rate_a || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block mb-1 font-medium">Rate B</label>
            <input
              type="number"
              name="rate_b"
              value={bikeData.rate_b || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
        
        <div>
          <label className="block mb-1 font-medium">Status</label>
          <select
            name="status"
            value={bikeData.status || 'available'}
            onChange={handleChange}
            className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
          >
            <option value="available">Available</option>
            <option value="rented">Rented</option>
            <option value="maintenance">Maintenance</option>
            <option value="retired">Retired</option>
          </select>
        </div>
        
        <div>
          <label className="block mb-1 font-medium">Notes</label>
          <textarea
            name="notes"
            value={bikeData.notes || ''}
            onChange={handleChange}
            className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            rows={3}
            placeholder="General notes about this bike"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium">Registration Expiry</label>
            <input
              type="date"
              name="rego_expiry"
              value={bikeData.rego_expiry || ''}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block mb-1 font-medium">Last Service</label>
            <input
              type="date"
              name="last_service"
              value={bikeData.last_service || ''}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
        
        <div>
          <label className="block mb-1 font-medium">Service Centre</label>
          <input
            type="text"
            name="service_centre"
            value={bikeData.service_centre || ''}
            onChange={handleChange}
            className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            placeholder="Name of service centre"
          />
        </div>
        
        <div>
          <label className="block mb-1 font-medium">Service Notes</label>
          <textarea
            name="service_notes"
            value={bikeData.service_notes || ''}
            onChange={handleChange}
            rows={4}
            className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            placeholder="Enter service notes (one per line)"
          />
          <p className="text-sm text-gray-600 mt-1">Enter each service note on a new line</p>
        </div>
        
        <div className="flex gap-4 mt-6">
          <button
            type="button"
            className="bg-gray-300 hover:bg-gray-400 px-6 py-2 rounded transition-colors"
            onClick={() => navigate('/bikes')}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded transition-colors disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditBikeDetails;